import React, {useMemo} from "react";

import {DailyMenuSearchParam} from "@apps/daily-menu/interfaces";
import cx from "classnames";
import moment from "moment";
import {
  Link,
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {OrderResponseData} from "@globals/DailyOrders/interfaces";
import {EventOrigin, recordEvent} from "@globals/config/eventUtils";
import {HolidayDetailsItem} from "@globals/hooks/useMenuDates";
import {DateString} from "@globals/types/utilityTypes";

import NomiconIcon from "@components/NomiconIcon";
import TransparentButton from "@components/TransparentButton";

import DateItemOrders from "./DateItemOrders";
import styles from "./DateSelectorItem.scss";

export type DateSelectorItemProps = {
  date: DateString;
  isSelected: boolean;
  favoritesCount: number;
  orders?: OrderResponseData[];
  closedDates: DateString[];
  holidayDetails?: HolidayDetailsItem;
  onDateClick: () => void;
};

export default function DateSelectorItem({
  date,
  isSelected,
  favoritesCount,
  orders,
  closedDates,
  holidayDetails,
  onDateClick,
}: DateSelectorItemProps): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextSearchParams = new URLSearchParams(searchParams);

  const onFavoritesClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    recordEvent(EventOrigin.PAGE, "sort by favorites", true);
    nextSearchParams.set(DailyMenuSearchParam.Date, date);
    nextSearchParams.set(DailyMenuSearchParam.SortOrder, "My Favorites");
    navigate({
      pathname: "/",
      search: `?${createSearchParams(nextSearchParams)}`,
    });
  };

  const getLink = () => {
    nextSearchParams.set(DailyMenuSearchParam.Date, date);
    return `/?${createSearchParams(nextSearchParams)}`;
  };

  const hasOrders = Boolean(orders?.length);
  const showFavorites = Boolean(favoritesCount && !hasOrders);
  const isDateClosed = useMemo(() => {
    return closedDates.find((closedDate) => closedDate === date);
  }, [closedDates]);

  return (
    <Link
      to={getLink()}
      onClick={onDateClick}
      className={cx(styles.dateDisplayLink, isSelected && styles.selectedDate)}
    >
      <span className={styles.dateInfo}>
        <span
          className={cx(
            styles.date,
            styles.bold,
            isDateClosed && styles.closedDate
          )}
        >
          {moment(date, "YYYY-MM-DD").format("ddd M/D")}
          {hasOrders && (
            <NomiconIcon
              className={styles.dateCheck}
              icon="nomicon-check-filled"
            />
          )}
        </span>
        {isDateClosed && (
          <span className={cx(styles.closedDateInfo, styles.closedDate)}>
            {holidayDetails?.name || "Menu not available"}
          </span>
        )}
        {hasOrders && !isDateClosed && (
          <DateItemOrders orders={orders as OrderResponseData[]} />
        )}
      </span>
      {showFavorites && (
        <TransparentButton
          onClick={onFavoritesClick}
          aria-label="Sort by favorites"
          className={styles.favorites}
        >
          <NomiconIcon
            icon="nomicon-heart-filled"
            className={styles.favoritesIcon}
          />
          <span className={cx(styles.favoritesCount, styles.bold)}>
            {" "}
            {favoritesCount}
          </span>
        </TransparentButton>
      )}
    </Link>
  );
}
